import React, { useState } from "react";
import { useI18n } from "@/i18n";
import Seo from "@/components/Seo";
import { orgSchema, breadcrumb } from "@/lib/seo";
import api from "@/lib/api";
import { toast } from "sonner";
import { Phone, WhatsappLogo, EnvelopeSimple, MapPin, Clock, CircleNotch, PaperPlaneTilt } from "@phosphor-icons/react";
import { PHONE_DISPLAY, PHONE_TEL, WHATSAPP_URL, EMAIL, ADDRESS_LINE1, ADDRESS_LINE2, MAPS_URL } from "@/lib/contact";

const InfoCard = ({ icon: Icon, label, value, href, color = "#EF5A1E" }) => (
  <a href={href} target={href?.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
    className="group flex items-center gap-4 rounded-3xl border border-[#E9E5DE] bg-white p-5 hover:border-[#EF5A1E] transition-colors duration-200">
    <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: color + "1A" }}>
      <Icon size={20} weight="fill" style={{ color }} />
    </div>
    <div className="min-w-0">
      <p className="font-body text-xs text-[#5A607A] uppercase tracking-wider">{label}</p>
      <p className="font-heading font-semibold text-[#0B1533] break-words">{value}</p>
    </div>
  </a>
);

export default function Contact() {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) { toast.error(t("form_fill")); return; }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success(t("form_sent"));
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch { toast.error("Error"); } finally { setSending(false); }
  };

  const inputCls = "w-full bg-white border border-[#E9E5DE] rounded-2xl px-4 py-3 font-body text-sm text-[#0B1533] focus:border-[#EF5A1E] focus:ring-2 focus:ring-[#EF5A1E]/15 focus:outline-none transition-all duration-200";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="contact-page">
      <Seo
        title={t("seo_contact_title")}
        description={t("seo_contact_desc")}
        path="/contact"
        jsonLd={[orgSchema(), breadcrumb([{ name: "Home", path: "/" }, { name: t("nav_contact"), path: "/contact" }])]}
      />
      <p className="font-body text-sm text-[#EF5A1E] font-semibold mb-3">{t("contact_kicker")}</p>
      <h1 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl text-[#0B1533] leading-[1.05] max-w-2xl">{t("contact_title")}</h1>
      <p className="font-body text-[#5A607A] mt-5 text-lg max-w-2xl">{t("contact_sub")}</p>

      <div className="grid lg:grid-cols-2 gap-10 mt-12">
        <div className="space-y-4">
          <InfoCard icon={Phone} label={t("phone_label")} value={PHONE_DISPLAY} href={`tel:${PHONE_TEL}`} />
          <InfoCard icon={WhatsappLogo} label="WhatsApp" value={PHONE_DISPLAY} href={WHATSAPP_URL} color="#25D366" />
          <InfoCard icon={EnvelopeSimple} label="E-Mail" value={EMAIL} href={`mailto:${EMAIL}`} />
          <InfoCard icon={MapPin} label={t("address_label")} value={`${ADDRESS_LINE1}, ${ADDRESS_LINE2}`} href={MAPS_URL} />
          <div className="rounded-3xl bg-[#0B1533] p-6 flex items-center gap-3">
            <Clock size={22} weight="fill" className="text-[#EF5A1E]" />
            <div>
              <p className="font-body text-xs text-white/50 uppercase tracking-wider">{t("hours_label")}</p>
              <p className="font-heading font-semibold text-lg text-white">{t("hours_value")}</p>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="rounded-3xl border border-[#E9E5DE] bg-white p-8 elev-soft" data-testid="contact-form">
          <h2 className="font-heading font-semibold text-2xl tracking-tight text-[#0B1533]">{t("contact_form_title")}</h2>
          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            <input data-testid="contact-name" className={inputCls} placeholder={t("form_name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input data-testid="contact-phone" className={inputCls} placeholder={t("form_phone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <input type="email" data-testid="contact-email" className={inputCls + " mt-4"} placeholder={t("form_email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <textarea data-testid="contact-message" rows="5" className={inputCls + " mt-4"} placeholder={t("form_message")} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
          <button type="submit" disabled={sending} data-testid="contact-send"
            className="mt-5 inline-flex items-center gap-2 bg-[#EF5A1E] text-white font-body font-semibold rounded-full px-8 py-3.5 hover:bg-[#D2450F] transition-colors duration-200 disabled:opacity-50">
            {sending ? <CircleNotch size={16} className="animate-spin" /> : <PaperPlaneTilt size={16} weight="fill" />}
            {t("form_send")}
          </button>
        </form>
      </div>
    </div>
  );
}
